import React from 'react';
import { Trash2 } from 'lucide-react';
import { formatPrice } from '../../utils/priceUtils';

interface CartItemProps {
    item: {
        id: string;
        name: string;
        price: number;
        quantity: number;
        shopName: string;
        logo_url?: string;
        badge?: string;
        discountPercent?: number;
        discountLimit?: number;
        promoPrice?: number;
        appliedCoupon?: string;
        currency: string;
    };
    onUpdateQuantity: (id: string, quantity: number) => void;
    onRemove: (id: string) => void;
}

const CartItem: React.FC<CartItemProps> = ({ item, onUpdateQuantity, onRemove }) => {
    const originalPrice = item.price * item.quantity;

    // Determine discount status robustly
    const hasPromoPrice = item.promoPrice && item.promoPrice < item.price;
    const hasDiscountPercent = item.discountPercent && item.discountPercent > 0;
    const hasDiscount = hasPromoPrice || hasDiscountPercent;

    // Calculate effective price with quantity limit
    let totalDiscountedPrice = originalPrice;
    let qtyAtDiscount = 0;
    let displayPercent = item.discountPercent || 0;

    if (hasDiscount) {
        qtyAtDiscount = item.discountLimit ? Math.min(item.quantity, item.discountLimit) : item.quantity;
        const qtyRegular = item.quantity - qtyAtDiscount;

        let unitPriceDiscounted = 0;
        if (item.promoPrice) {
            unitPriceDiscounted = item.promoPrice;
            // Recalculate percent for display if missing
            if (!displayPercent && item.price > 0) {
                displayPercent = Math.round(((item.price - item.promoPrice) / item.price) * 100);
            }
        } else {
            unitPriceDiscounted = item.price * (1 - (displayPercent / 100));
        }

        totalDiscountedPrice = (qtyAtDiscount * unitPriceDiscounted) + (qtyRegular * item.price);
    }

    return (
        <div className="flex gap-4 group relative py-2">
            <div className="w-20 h-20 bg-white border border-gray-100 rounded-xl overflow-hidden flex-shrink-0 p-2 relative">
                <img
                    src={item.logo_url || 'https://placehold.co/100x100?text=' + item.name}
                    alt={item.name}
                    className="w-full h-full object-contain"
                />
                {item.badge && (
                    <div className="absolute top-0 right-0 bg-green-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-bl-lg shadow-sm z-10">
                        {item.badge}
                    </div>
                )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                    <h4 className="font-bold text-gray-900 truncate text-sm">{item.name}</h4>
                    <p className="text-[10px] text-purple-600 font-bold uppercase tracking-wider">{item.shopName}</p>
                </div>

                <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1 border border-gray-100 h-8">
                        <button
                            onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                            className="w-6 h-full flex items-center justify-center hover:bg-white rounded transition-colors text-gray-500 text-sm font-bold"
                        >
                            -
                        </button>
                        <span className="text-xs font-black w-4 text-center">{item.quantity}</span>
                        <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                            className="w-6 h-full flex items-center justify-center hover:bg-white rounded transition-colors text-gray-500 text-sm font-bold"
                        >
                            +
                        </button>
                    </div>
                    <div className="text-right">
                        {hasDiscount ? (
                            <div className="flex flex-col items-end leading-none">
                                <span className="text-[10px] text-gray-400 line-through font-medium">{formatPrice(originalPrice, item.currency)}</span>
                                <span className="text-sm font-black text-red-500">{formatPrice(totalDiscountedPrice, item.currency)}</span>
                                {item.discountLimit && item.quantity > item.discountLimit && (
                                    <span className="text-[9px] text-orange-500 font-bold">Max {item.discountLimit} à -{displayPercent}%</span>
                                )}
                            </div>
                        ) : (
                            <p className="font-bold text-gray-900 text-sm">{formatPrice(originalPrice, item.currency)}</p>
                        )}
                    </div>
                </div>
            </div>
            <button
                onClick={() => onRemove(item.id)}
                className="absolute -top-2 -right-2 p-1.5 bg-white shadow-sm rounded-full text-gray-400 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100 scale-90 hover:scale-100 border border-gray-100"
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
};

export default CartItem;
